/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import util from "../util/util.js";
import fieldManager from "../util/fields.js";
import validate from "../util/validate.js";
import googleMaps from "../util/google-maps.js";
import h from "util/dom-builder.js";

const program = {
	code: "powl",
	name: "Powerful Owl Project",
	description: "The Powerful Owl Project aims to provide a greater understanding of the ecology of urban Powerful Owl populations and engage the community in the conservation of Powerful Owls and their habitat. <a href=' https://birdlife.org.au/projects/powerful-owl-project/' target='_blank'>Find out more&hellip;</a>",
	// canParticipate: function(user) {
	// 	// Temporary - only show to a few users for testing
	// 	return _.contains([7797, 10089, 11382, 16348, 2894, 20301, 8834, 7072, 19471, 5998], user.id);
	// 	// return _.any(user.permissions, function(p) {
	// 	// 	return p.type == "program" && p.program == "powl";
	// 	// });
	// },
	canFilterBy: function(user) {
		return user.is_administrator || user.permissions?.some(p => p.program === 'powl' && p.role === 'admin');
	},
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.options = {
				detection_method: lookups.detection_method.filter(x => x.code != 'captured'),
				activity: [
					"Roosting",
					"Roosting together",
					"Holding prey/feeding",
					"Flying in",
					"Flying out",
					"Investigating hollow",
					"Male bringing food to hollow",
					"Female in hollow",
					"Chick/(s) trilling in hollow",
					"Chick outside hollow",
					"Chick/(s) fledged",
					"Calling",
					"Preening",
					"Single owl calling",
					"Calling together",
					"Attending young",
					"Other"
				].map(x => ({
					name: x,
					code: x.toLowerCase().replace(/[^a-z0-9]+/g, '_')
				})),
				habitat: [
					"An urban garden or similar",
					"Urban open or green space",
					"Bush block or forest",
					"Mangrove",
					"Other"
				].map(x => ({
					name: x,
					code: x.toLowerCase().replace(/[^a-z0-9]+/g, '_')
				})),
				location_type: lookups.powl_location_type,
				experience: lookups.powl_experience,
				breeding_activity: lookups.powl_breeding_activity,
				evidence: lookups.powl_evidence,
				tree_species: [
					"None",
					"Sydney Red Gum",
					"Blackbutt",
					"Sydney Peppermint",
					"Coachwood",
					"Broad-leaved Privet",
					"Small-leaved Privet",
					"Sweet Pittosporum",
					"Black Wattle",
					"Cheese Tree",
					"She Oak",
					"Tallowwood",
					"Blue Gum",
					"Fig",
					"Liquid Amber",
					"Jacaranda",
					"Melaleuca",
					"Lili Pili",
					"Bunya Pine",
					"Unknown",
					"Other"
				].map(x => ({
					name: x,
					code: x.toLowerCase().replace(/[^a-z0-9]+/g, '_')
				})),
				prey_species: [
					"None",
					"Ringtail possum",
					"Brushtail possum",
					"Flying fox",
					"Rabbit",
					"Rat",
					"Australian magpie",
					"Pied Currawong",
					"Sulphur-crested Cockatoo",
					"Rainbow Lorikeet",
					"Galah",
					"Noisy Miner",
					"Brush Turkey",
					"Sugar Glider",
					"Other"
				].map(x => ({
					name: x,
					code: x.toLowerCase().replace(/[^a-z0-9]+/g, '_')
				}))
			};
			return true;
		});
	},
	programData: function(survey) {
		var fields = program.fields();

		var passedQuiz = ko.observable(null);
		birdata.passedQuiz('powl').then(passedQuiz).catch(e => console.log(e));

		var mustTakeQuiz = ko.pureComputed(function() {
			return survey.type() && survey.type().code == 'breeding_territory_monitoring' && passedQuiz() === false;
		});

		function takeQuiz() {
			var div = h('div', { 'class': 'birdata-embed' }, h('div', { 'class': 'modal', 'data-bind': 'component: { name: "quiz", params: quizParams }' }));
			document.body.appendChild(div);

			ko.applyBindings({
				quizParams: {
					quiz: 'powl',
					onCompletion: function() {
						div.parentNode.removeChild(div);
						birdata.passedQuiz('powl').then(passedQuiz).catch(e => console.log(e));
					}
				}
			}, div);
		}

		var showTerritoryField = ko.pureComputed(function() {
			return survey.type() && survey.type().code == 'breeding_territory_monitoring';
		});

		var enableTerritoryField = ko.pureComputed(function() {
			return !!survey.surveyPoint();
		});

		setupTerritoryField(fields.territory.field, survey);

		return {
			fields,
			validations: _.values(fields),
			passedQuiz,
			mustTakeQuiz,
			takeQuiz,
			enableTerritoryField,
			showTerritoryField
		};
	},
	fields: function() {
		return program.surveyFields();
	},
	surveyFields: function() {
		var fieldDefinitions = [
			{
				name: "experience",
				type: "options",
				label: "Observation skills",
				options: program.options.experience
			},
			{
				name: "territory",
				type: "custom",
				help: "If you know the name of the territory surveyed, select it from the list or define a new territory by selecting <em>New Territory</em>. If you are unsure, please leave this field set to <em>None</em>.",
				formattedValue: function(v) {
					return v ? v.name : "";
				},
				validate: function(field) {
					var v = field.value();
					if(v && !v.id && (v.name || "").trim() == "") {
						return "New territory name is required";
					}
				}
			},
			{
				name: "prey",
				type: "text"
			},
			{
				name: "treeSpecies",
				help: "See <a href='http://birdlife.org.au/documents/POW-PowerfulOwl-nest-roosttree-species.pdf' target='_blank'>Tree identification guide</a>",
				type: "text"
			},
			{
				name: "distanceToUrbanBoundaryInMetres",
				label: "Distance to urban boundary",
				type: "number",
				validate: ">=0,<1000000",
				units: "m"
			},
			{
				name: "habitat",
				type: "text"
			},
			{
				name: "locationType",
				label: "Location detail",
				type: "options",
				options: program.options.location_type
			},
			{
				name: "pelletsCollected",
				type: "boolean",
				defaultValue: false
			},
			{
				name: "distanceToWaterInMetres",
				label: "Distance to water",
				type: "number",
				validate: ">=0,<100000",
				units: "m"
			}
		];

		var fields = _.object(
			fieldDefinitions.map(def => def.name),
			fieldDefinitions.map(fieldManager.field)
		);

		setupMenuWithOther(fields, 'habitat', program.options.habitat);
		setupMenuWithOther(fields, 'treeSpecies', program.options.tree_species);
		setupMenuWithOther(fields, 'prey', program.options.prey_species);

		return fields;
	},
	sightingFields: function() {
		var fieldDefinitions = [
			{
				name: "adultCount",
				type: "integer",
				label: "Adults",
				validate: ">=0"
			},
			{
				name: "juvenileCount",
				type: "integer",
				label: "Juveniles",
				validate: ">=0"
			},
			{
				name: "activity",
				label: "Behaviour",
				type: "text",
				options: program.options.activity,
				// validate: "required"
			},
			{
				name: "evidence",
				label: "Detection method",
				type: "options",
				options: program.options.evidence
			}
		];

		var fields = _.object(
			fieldDefinitions.map(def => def.name),
			fieldDefinitions.map(fieldManager.field)
		);

		setupMenuWithOther(fields, 'activity', program.options.activity);

		return fields;
	},
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().powl.fields;
		json.powl = {};
		_.each(fields, function(f) {
			f.field.save(json.powl);
		});
	},
	loadFromJSON: function(json, fields, survey) {
		// Because we have a custom list of survey types, we need to manually set the survey type here:
		// (TODO: see if there is a cleaner way to solve this)
		if(survey) {
			var type = _.findWhere(program.surveyTypeOptions, { code: json.survey_type});
			survey.type(type);
		}

		var data = json.powl;

		if(!data) return; //incomplete survey

		_.each(fields, function(f) {
			f.field.load(data);
		});
	},
	visibleSharedSiteLayers: function() {
		return [];
	},
	updateMap: function(map, survey, areasPromise, locationViewModel) {
	},
	surveyTypeOptions: [
		{
			name: 'Incidental sighting',
			code: 'incidental_search'
		}, {
			name: 'Breeding territory monitoring',
			code: 'breeding_territory_monitoring'
		}
	],
	afterSelect: function(survey) {
		// Set survey type after program selected
		if(!survey.type() || !_.contains(['incidental_search', 'breeding_territory_monitoring'], survey.type().code)) {
			survey.type(_.findWhere(program.surveyTypeOptions, { 'code': 'breeding_territory_monitoring' }));
		}
		survey.allSpeciesRecorded(false);

		// Note: see survey-edit-sightings for further powl-specific logic
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	multipleDaysEnabled: function(survey) {
		return false;
	},
	allSpeciesRecordedEnabled: function(survey) {
		return false;
	}
	// requireSightingCount: true
};

function setupTerritoryField(field, survey) {
	var newTerritoryOption = { name: 'New Territory' };
	field.menuOptions = ko.observable([newTerritoryOption]);
	survey.surveyPoint.subscribe(function(newValue) {
		if(newValue) {
			birdata.powlTerritories(newValue.lat, newValue.lon).then(function(territories) {
				// Have to make sure that the current value is included in the territories menu otherwise it will get unset
				var currentValue = field.value();
				if(currentValue && currentValue.id) {
					territories = territories.map(t => t.id == currentValue.id ? currentValue : t);
				} else if(!currentValue && territories.length == 1) {
					// Set default territory
					field.value(territories[0]);
				}

				territories.push(newTerritoryOption);

				field.menuOptions(territories);
			});
		} else {
			field.menuOptions([newTerritoryOption]);
		}
	});
	field.menuValue = ko.observable();
	field.otherValue = ko.observable();
	field.otherSelected = ko.pureComputed(() => {
		return field.menuValue() && field.menuValue().id === undefined;
	});

	field.value.subscribe(function(x) {
		if(!x) {
			field.menuValue(undefined);
		} else if(x.id) {
			field.menuValue(x);
		} else {
			field.menuValue(newTerritoryOption);
			field.otherValue(x.name);
		}
	});

	ko.computed(function() {
		var x = field.otherSelected() ? { name: field.otherValue() } : field.menuValue();

		// Prevent infinite recursion.
		// KO always treats setting an object as a change on the observable even if it's exactly the same object instance.
		if(field.value.peek() != x) {
			field.value(x);
		}
	});
}

// Set up a select menu with an other option that brings up a text field
function setupMenuWithOther(fields, fieldName, options) {
	var field = fields[fieldName].field;
	field.menuOptions = options;
	field.menuValue = ko.observable();
	field.otherValue = ko.observable();
	field.menuRenderFn = option => option.name;
	field.otherSelected = ko.pureComputed(() => {
		return field.menuValue() && field.menuValue().code == 'other';
	});

	field.value.subscribe(value => {
		if(value === null) {
			field.menuValue(null);
			return;
		}

		var menuVal = field.menuValue.peek();
		if(menuVal && menuVal.code == 'other') {
			field.otherValue(value);
		} else {
			var option = _.findWhere(options, { name: value });

			// Note: if we get infinite recursion issues here, consider similar fix to territory field
			if(option) {
				field.menuValue(option);
			} else {
				field.menuValue(_.findWhere(options, { code: 'other' }));
				field.otherValue(value);
			}
		}
	});

	ko.computed(function() {
		if(field.menuValue()) {
			if(field.menuValue().code == 'other') {
				field.value(field.otherValue());
			} else {
				field.value(field.menuValue().name);
			}
		} else {
			field.value(null);
		}
	});

}

export default program;
